import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { toast } from 'react-toastify';
import Button from '../../../../01_atoms/Button';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../../utils/behaviorSettings';
import {
  composeValidators,
  validEmail,
  required,
  minDigits,
  maxDigits,
} from '../../../../../utils/formFieldValidators';
import { parsePhone, parseSpaceless } from '../../../../../utils/formFieldParsers';
import { pushFormSubmission as gtmPushFormSubmission } from '../../../../../utils/gtm';
import fetch from '../../../../../utils/fetch';
import unexpectedErrorMessage from '../../../../../utils/unexpectedErrorMessage';
import { CheckboxesField, TextField } from '../../../../01_atoms/FormElements';
import { DEFAULT_COUNTRY_CODE } from '../../../../../utils/constants';
import Address from '../../../AddressAutocompleteSearch/Address';
import { getDefaultCountry } from '../../../../../utils/address-schema';
import { propTypes as defaultPicturePropTypes } from '../../../../01_atoms/DefaultPicture';
import { propTypes as linkPropTypes } from '../../../../01_atoms/Link';
import HeroSocialAdWrapper from '../../BBHero/BBHeroSocialAd/HeroSocialAdWrapper';
import FormattedText from '../../../../01_atoms/FormattedText';
import { getDefaultDescriptionForContactPreferences } from '../../../DonationForm/steps/03_ContactPreferencesStep';
import PhoneIcon from '../../../../../public/static/icons/phone-2024.svg';
import LetterIcon from '../../../../../public/static/icons/letter.svg';

import s from './index.module.scss';
import socialAdStyles from '../../BBHero/BBHeroSocialAd/index.module.scss';

const BBDotdigitalContactForm = ({
  heading = '',
  subheading = '',
  addressBookId = 0,
  optInType = 'Single',
  logoLink,
  uuid = null,
  image,
  sectionContent,
  title,
  label = '',
  behaviorSettings = null,
}) => {
  const id = useId();

  const classes = [
    'bb',
    s['bb-dotdigital-contact-form'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  const onSubmit = (data, form) => {
    const addressObject = data.address;
    // Get rid of country code, to avoid it to be added to the resulted string.
    addressObject.country = addressObject.country.label;
    const getAddressParts = (obj, parts) => {
      Object.values(obj).forEach((item) => {
        if (item && typeof item === 'string') {
          parts.push(item);
          return;
        }
        if (typeof item === 'object' && !Array.isArray(item)) {
          getAddressParts(item, parts);
        }
      });
      return parts;
    };
    const addressParts = getAddressParts(data.address, []);
    const payload = {
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      phone_drupal: data.phone,
      address_drupal: addressParts.join(', '),
      optin_email_drupal: data.optin_email?.length ? 1 : 0,
      address_book_id: addressBookId,
      opt_in_type: optInType,
    };

    // Generating form ID for GTM from block title and address book ID.
    const formId = `${title.replace(/\W/g, '_').toLowerCase()}_${addressBookId}`;
    return new Promise((resolve, reject) => {
      // Send an event to GTM.
      gtmPushFormSubmission(formId);
      fetch('/dotdigital-subscribe', {
        method: 'POST',
        throwNotOk: false,
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((resp) => {
          if (resp.statusCode === 200) {
            toast.success('Thank you for subscribing!', { autoClose: 7000 });
            resolve(resp);
            return resp;
          }

          if (resp.statusCode === 400 && resp.body === 'Invalid email address') {
            toast.error(
              `The ${data.email} email address is not allowed. Please try to use another email address.`,
              { autoClose: 7000 },
            );
          } else {
            toast.error(
              <div
                dangerouslySetInnerHTML={{
                  __html: `Sorry, something went wrong. ${unexpectedErrorMessage()}`,
                }}
              />,
            );
          }

          resolve();
          return resp;
        })
        .then((resp) => {
          if (resp.statusCode === 200) {
            form.restart();

            // if (form.getFieldState('firstname')) {
            //   form.resetFieldState('firstname');
            // }
            //
            // if (form.getFieldState('lastname')) {
            //   form.resetFieldState('lastname');
            // }
            //
            // form.resetFieldState('email');
          }
        })
        .catch((error) => {
          toast.error(
            <div
              dangerouslySetInnerHTML={{
                __html: `Sorry, something went wrong. ${unexpectedErrorMessage()}`,
              }}
            />,
          );
          reject(error);
          return error;
        });
    });
  };

  return (
    <HeroSocialAdWrapper
      heading={heading}
      image={image}
      logoLink={logoLink}
      contentRight={
        <>
          {label && <div className={socialAdStyles['bb-hero-social-ad__label']}>{label}</div>}
          {sectionContent.type === 'text' && (
            <FormattedText
              className={socialAdStyles['bb-hero-social-ad__highlighted-text']}
              text={sectionContent.text}
            />
          )}
          {sectionContent.type === 'list' && (
            <ul
              className={`${socialAdStyles['bb-hero-social-ad__list']} ${
                socialAdStyles[`bb-hero-social-ad__list--${sectionContent.variant}`]
              }`}
            >
              {sectionContent.items.map(
                (item) =>
                  item.title &&
                  item.text && (
                    <li className={socialAdStyles['bb-hero-social-ad__list-item']} key={item.title}>
                      {item.title && (
                        <h3 className={socialAdStyles['bb-hero-social-ad__list-item-title']}>
                          {item.title}
                        </h3>
                      )}
                      {item.text && (
                        <FormattedText
                          className={socialAdStyles['bb-hero-social-ad__list-item-text']}
                          text={item.text}
                        />
                      )}
                    </li>
                  ),
              )}
            </ul>
          )}
        </>
      }
      classnames={classes.join(' ')}
      contentLeft={
        <div className={s['bb-dotdigital-contact-form__form']} id={uuid}>
          <div className={s['bb-dotdigital-contact-form__form-header']}>
            {title && <h2 className={s['bb-dotdigital-contact-form__form-heading']}>{title}</h2>}
            {subheading && (
              <p className={s['bb-dotdigital-contact-form__form-subheading']}>{subheading}</p>
            )}
          </div>

          <Form
            initialValues={{
              address: {
                country: getDefaultCountry(),
              },
            }}
            mutators={{
              // Create a mutator which allows to modify field's value by external callbacks.
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
            onSubmit={onSubmit}
            subscription={{ submitting: true, valid: true }}
            render={({ handleSubmit, submitting, valid }) => (
              <form onSubmit={handleSubmit}>
                <TextField
                  label="First name"
                  name="firstname"
                  placeholder="Jane"
                  color="green"
                  validate={required()}
                  isRequired={true}
                  id={`firstname-${id}`}
                />
                <TextField
                  label="Last name"
                  name="lastname"
                  placeholder="Bloggs"
                  color="green"
                  validate={required()}
                  isRequired={true}
                  id={`lastname-${id}`}
                />
                <TextField
                  label="Email address"
                  name="email"
                  placeholder="jane@email.com"
                  color="green"
                  parse={parseSpaceless}
                  validate={composeValidators(required(), validEmail())}
                  isRequired={true}
                  id={`email-${id}`}
                  icon={<LetterIcon />}
                />

                <TextField
                  name="phone"
                  label="Phone number"
                  placeholder={DEFAULT_COUNTRY_CODE === 'GB' ? '+44 1 000 0000' : '+353 1 000 0000'}
                  parse={parsePhone}
                  validate={
                    DEFAULT_COUNTRY_CODE === 'GB'
                      ? minDigits(7, 'Please enter a valid phone number with at least 7 digits')
                      : composeValidators(
                          required('Please enter a phone number in order to proceed'),
                          minDigits(7, 'Please enter a valid phone number with at least 7 digits'),
                          maxDigits(
                            18,
                            'Please enter a valid phone number with no longer than 18 digits',
                          ),
                        )
                  }
                  isRequired={DEFAULT_COUNTRY_CODE !== 'GB'}
                  tooltip={
                    DEFAULT_COUNTRY_CODE === 'GB'
                      ? undefined
                      : 'This is in case we need to contact you about your donation.'
                  }
                  icon={<PhoneIcon />}
                  inputMode="numeric"
                  pattern="[0-9+\s\-\(\)\.]+"
                  infoOnPatternNotMatched="Please use numbers only"
                />

                <Address />

                <CheckboxesField
                  name="optin_email"
                  options={[
                    {
                      value: 'optin_email',
                      label:
                        'Yes, I want to hear from Concern by email about their work and how I can continue to help change lives',
                    },
                  ]}
                  label=""
                  withBackground
                />
                <div className={s['bb-dotdigital-contact-form__form-optin-description']}>
                  {getDefaultDescriptionForContactPreferences()}
                </div>

                <div className={s['bb-dotdigital-contact-form__form-submit-button-wrapper']}>
                  <Button type="primary" isMobileBlock isLoading={submitting}>
                    Submit
                  </Button>
                </div>
              </form>
            )}
          />
        </div>
      }
    />
  );
};

BBDotdigitalContactForm.propTypes = {
  logoLink: PropTypes.shape(linkPropTypes).isRequired,
  heading: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string,
  addressBookId: PropTypes.number,
  optInType: PropTypes.oneOf(['Single', 'Double', 'VerifiedDouble']),
  subheading: PropTypes.string,
  image: PropTypes.shape(defaultPicturePropTypes).isRequired,
  uuid: PropTypes.string,
  sectionContent: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.oneOf(['text']),
      text: PropTypes.string.isRequired,
    }),
    PropTypes.shape({
      type: PropTypes.oneOf(['list']),
      variant: PropTypes.oneOf(['green', 'yellow']).isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          text: PropTypes.string,
        }),
      ),
    }),
  ]).isRequired,
  behaviorSettings: behaviorSettingsProps,
};

export default BBDotdigitalContactForm;
